<!-- SiteFooter.vue -->
<template>
  <footer class="site-footer">
    <div class="footer-card">
      <div class="footer-top">
        <!-- Brand -->
        <div class="footer-brand">
          <div class="footer-brand-mark">
            <img :src="logo" alt="Newarc" />
          </div>
          <p class="footer-claim">
            Se puoi immaginarla,<br />
            puoi realizzarla.
          </p>
          <div class="footer-socials">
            <a v-for="s in socials" :key="s.label" :href="s.href" class="footer-social" :aria-label="s.label" v-html="s.icon"></a>
          </div>
          <div class="footer-contacts">
            <h3 class="footer-contacts-title">Sede Operativa</h3>
            <p>Corso Ferrucci 36, 10138 Torino</p>
            <p><a class="footer-phone" href="tel:+390110263850" @click="trackContact('contact_phone_click')">011 026 3850</a></p>
          </div>
          <div class="footer-contacts footer-contacts--legal">
            <h3 class="footer-contacts-title">Sede Legale</h3>
            <p>Via Vittorio Emanuele II 29, 10023 Chieri (TO)</p>
          </div>
        </div>

        <!-- 3 sub-columns -->
        <div class="footer-cols">
          <div v-for="col in columns" :key="col.title" class="footer-col">
            <h3>{{ col.title }}</h3>
            <ul>
              <li v-for="l in col.links" :key="l.label">
                <a v-if="l.href" :href="l.href" @click="onFooterLink(l)">{{ l.label }}</a>
                <a v-else-if="l.soon" href="#">{{ l.label }}</a>
                <router-link v-else :to="l.to">{{ l.label }}</router-link>
              </li>
            </ul>
            <div v-if="col.sub" class="footer-subgroup">
              <h3>{{ col.sub.title }}</h3>
              <ul>
                <li v-for="l in col.sub.links" :key="l.label">
                  <a v-if="l.href" :href="l.href">{{ l.label }}</a>
                  <a v-else-if="l.soon" href="#">{{ l.label }}</a>
                  <router-link v-else :to="l.to">{{ l.label }}</router-link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <span>ImmaginaCasa è un marchio di Newarc S.r.l. — P.IVA e C.F. 12533550013 — REA TO-1297236 — Cap. soc. € 13.069,91 i.v.</span>
        <div class="footer-legal-links">
          <router-link to="/termini-e-condizioni">Condizioni d'uso</router-link>
          <router-link to="/privacy-policy">Privacy Policy</router-link>
          <router-link to="/cookie-policy">Cookie Policy</router-link>
          <router-link to="/note-legali">Note legali</router-link>
          <router-link to="/segnala">Segnala un contenuto</router-link>
          <button type="button" class="footer-legal-btn" @click="openCookieSettings">Impostazioni cookie</button>
          <language-switcher variant="footer" />
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import logo from '@/assets/img/newarc-logo-2025.png';
import { trackEvent } from '@/services/analytics';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';

export default {
  name: 'SiteFooter',
  components: { LanguageSwitcher },
  methods: {
    trackContact(name) {
      trackEvent(name);
    },
    onFooterLink(l) {
      const href = (l && l.href) || '';
      if (href.startsWith('mailto:')) trackEvent('contact_email_click');
      else if (href.startsWith('tel:')) trackEvent('contact_phone_click');
    },
    openCookieSettings() {
      // Reopen the consent preferences (CookieConsentBanner listens for this).
      window.dispatchEvent(new CustomEvent('ic:open-cookie-preferences'));
    },
  },
  data() {
    return {
      logo,
      socials: [
        { label: 'Facebook', href: '#', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 22v-8.5h2.86l.43-3.34h-3.29V8.04c0-.96.27-1.62 1.66-1.62H16.9V3.44c-.32-.04-1.41-.13-2.7-.13-2.66 0-4.5 1.62-4.5 4.61v2.24H6.83v3.34h2.87V22h3.8Z"/></svg>' },
        { label: 'Instagram', href: '#', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" stroke="none"/></svg>' },
        { label: 'TikTok', href: '#', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19.6 8.3a6.5 6.5 0 0 1-3.84-1.27V15.8a4.83 4.83 0 1 1-4.83-4.83c.3 0 .58.03.86.08v2.55a2.4 2.4 0 0 0-.86-.16 2.36 2.36 0 1 0 2.36 2.36V2.5h2.43a4.07 4.07 0 0 0 3.88 3.34V8.3Z"/></svg>' },
      ],
      // Ogni link: { label, to }. `soon: true` = pagina ancora da creare
      // (il link non naviga, ma `to` fissa già l'URL previsto per quando esisterà).
      columns: [
        {
          title: 'Trova casa',
          links: [
            { label: 'Tutti gli annunci', to: '/annunci' },
            { label: 'Case in vendita', to: '/annunci' },
            { label: 'I tuoi preferiti', to: '/user/favorite' },
            { label: 'Ricerche salvate (Ping!)', to: '/user/ping' },
            { label: 'Prenota una videochiamata', to: '/prenota-videocall' },
          ],
        },
        {
          title: 'Ristruttura',
          links: [
            { label: 'Richiedi preventivo online', to: '/configure/conferma-indirizzo' },
            { label: 'I tuoi preventivi', to: '/user/preventivi-ristrutturazione' },
          ],
          sub: {
            title: 'Scopri',
            links: [
              { label: 'Vendi casa', to: '/vendi' },
              { label: 'Chi siamo', to: '/chi-siamo', soon: true },
              { label: 'Sei un\'agenzia?', to: '/agenzie' },
              { label: 'FAQ', to: '/faq' },
              { label: 'Contattaci', href: 'mailto:info@newarc.it' },
              { label: 'Accedi', to: '/iLogin' },
            ],
          },
        },
      ],
    };
  },
};
</script>

<style scoped>
/* Design tokens — se li hai già globali, rimuovi questo :root */
.site-footer {
  --background: #fbf9f5;
  --foreground: #000;
  --newarc-bone: #fff;
  --accent: #499b79;
  --newarc-gray-1: #f3f1ec;
  --newarc-gray-2: #e8e5dd;
  --newarc-gray-3: #a8a59d;
  --newarc-gray-text: #4a4a48;
  --border: #e8e5dd;
  --muted: #f3f1ec;
  --radius-sm: 8px;
  font-family: 'Raleway', sans-serif;
  padding: 64px;
}
.footer-card { background: var(--newarc-bone); border-radius: 28px; padding: 35px 35px 28px; display: flex; flex-direction: column; gap: 64px; }
.footer-top { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: start; }
.footer-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: start; }
.footer-brand { display: flex; flex-direction: column; }
.footer-brand-mark img { width: 180px; height: auto; display: block; }
.footer-claim { margin: 8px 0 0; font: 600 18px/1.45 'Raleway', sans-serif; letter-spacing: -0.005em; color: var(--foreground); max-width: 480px; }
.footer-socials { display: flex; gap: 10px; margin: 18px 0 40px; }
.footer-social { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 999px; background: var(--accent); color: var(--background); transition: transform 200ms ease; }
.footer-social :deep(svg) { width: 16px; height: 16px; }
.footer-social:hover { transform: scale(1.08); }
.footer-contacts { display: flex; flex-direction: column; gap: 6px; }
.footer-contacts-title { margin: 0 0 6px; font: 600 14px/1 'Raleway', sans-serif; color: var(--foreground); }
.footer-contacts p { margin: 0; font: 400 14px/1.4 'Raleway', sans-serif; color: var(--newarc-gray-text); }
.footer-phone { color: inherit; text-decoration: none; transition: color 150ms ease; }
.footer-phone:hover { color: var(--accent); }
.footer-contacts--legal { margin-top: 24px; }
.footer-col { display: flex; flex-direction: column; }
.footer-col h3 { margin: 0 0 10px; font: 600 15px/1 'Raleway', sans-serif; color: var(--foreground); }
.footer-col ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
.footer-col ul a { font: 400 15px/1.3 'Raleway', sans-serif; color: var(--newarc-gray-text); text-decoration: none; transition: color 150ms ease; }
.footer-col ul a:hover { color: var(--foreground); }
.footer-subgroup { margin-top: 32px; }
.footer-bottom { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding-top: 28px; font: 400 13px/1.4 'Raleway', sans-serif; color: var(--newarc-gray-3); }
.footer-legal-links { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 12px 24px; }
.footer-legal-links a { color: var(--newarc-gray-3); font-size: 13px; text-decoration: none; transition: color 150ms ease; }
.footer-legal-links a:hover { color: var(--foreground); }
.footer-legal-btn { background: none; border: 0; padding: 0; cursor: pointer; color: var(--newarc-gray-3); font: 400 13px/1 'Raleway', sans-serif; transition: color 150ms ease; }
.footer-legal-btn:hover { color: var(--foreground); }

/* ---- Responsive: da 1024px in su il layout resta identico a sopra. ---- */

/* Tablet (≤1023px): brand sopra, colonne link sotto (ancora affiancate). */
@media (max-width: 1023.98px) {
  .site-footer { padding: 40px 24px; }
  .footer-top { grid-template-columns: 1fr; gap: 40px; }
  .footer-card { gap: 48px; }
}

/* Mobile (≤767px): tutto in colonna singola, padding compatti, footer-bottom
   impilato. Nessun overflow orizzontale fino a 320px. */
@media (max-width: 767.98px) {
  .site-footer { padding: 20px 14px; }
  .footer-card { border-radius: 20px; padding: 26px 22px 22px; gap: 36px; }
  .footer-top { gap: 36px; }
  .footer-cols { grid-template-columns: 1fr; gap: 28px; }
  .footer-brand-mark img { width: 160px; }
  .footer-claim { font-size: 17px; }
  .footer-socials { margin: 16px 0 28px; }
  .footer-subgroup { margin-top: 28px; }
  .footer-bottom {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    padding-top: 22px;
    line-height: 1.5;
  }
  .footer-legal-links { justify-content: flex-start; gap: 12px 24px; }
}
</style>
